"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useAuthSession } from "@/app/components/auth/useAuthSession";
import BookingDetailsCard from "./BookingDetailsCard";
import { siteName } from "@/lib/site";
import { strings } from "@/content/strings";
import { formatTimeWithZone, formatTimeZoneLabel } from "@/lib/dates";

type BookingView = {
  id: string;
  status: string;
  displayStatus?: string;
  checkIn: string;
  checkOut: string;
  checkInTime: string;
  checkOutTime: string;
  nights: number;
  numberOfGuests: number;
  specialRequests?: string | null;
  cancellationReason?: string | null;
  payment?: {
    hasPending: boolean;
    amountDue?: number;
    currency?: string;
    dueDate?: string;
    type?: string;
    isPayable?: boolean;
    nextDueDate?: string;
    nextAmountDue?: number;
    nextType?: string;
  };
  payments?: Array<{
    amount: number;
    currency?: string | null;
    dueDate: string;
    type: string;
    status: string;
    refundedAmount?: number | null;
    refundedAt?: string | null;
    refunds?: Array<{
      id: string;
      amount: number;
      currency?: string | null;
      refundedAt?: string | null;
      reason?: string | null;
    }>;
  }>;
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string | null;
  };
  property: {
    id: string;
    name: string;
    slug?: string | null;
    address?: Record<string, any> | null;
    timeZone?: string | null;
  };
  ratePlan: {
    name: string;
    description?: string | null;
  };
  priceBreakdown: Record<string, any>;
  lockAccesses?: Array<{
    id: string;
    deviceName: string;
    codes?: string[];
    status: string;
    startsAt: string;
    endsAt: string;
  }>;
};

type VerificationDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export default function BookingLookup({ bookingId }: { bookingId?: string }) {
  const bookingStrings = strings.screens.bookingDetail;
  const searchParams = useSearchParams();
  const { data: session, status } = useAuthSession();
  const [bookings, setBookings] = useState<BookingView[]>([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [verifiedInfo, setVerifiedInfo] = useState<VerificationDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const resolvedBookingId = bookingId || searchParams.get("bookingId") || undefined;
  const callbackUrl = resolvedBookingId ? `/bookings/${resolvedBookingId}` : "/bookings";
  const loginUrl = `/login?callbackUrl=${encodeURIComponent(callbackUrl)}&contextName=${encodeURIComponent(siteName)}`;

  const isLoggedIn = !!session?.user?.email;
  const isSessionLoading = status === "loading";

  const loadBookings = async (params?: Record<string, string>) => {
    setLoading(true);
    setError("");
    try {
      const query = params ? `?${new URLSearchParams(params).toString()}` : "";
      const res = await fetch(`/api/guest/bookings${query}`);
      const data = await res.json();
      if (!data.success) {
        setError(data.error || bookingStrings.loadBookingsError);
        return;
      }
      setBookings(data.data as BookingView[]);
      setVerifiedInfo(null);
    } catch (err) {
      setError(bookingStrings.loadBookingsError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || status === "loading") return;
    const params: Record<string, string> = {};
    if (resolvedBookingId) params.bookingId = resolvedBookingId;
    void loadBookings(params);
  }, [isLoggedIn, status, resolvedBookingId]);

  const handleLookup = async () => {
    setLoading(true);
    setError("");
    setSubmitted(true);
    try {
      const res = await fetch("/api/guest/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: resolvedBookingId,
          firstName,
          lastName,
          email,
          phone,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || bookingStrings.verifyError);
        return;
      }
      setBookings(data.data as BookingView[]);
      setVerifiedInfo({ firstName, lastName, email, phone });
      if ((data.data as BookingView[]).length === 0) {
        setError(bookingStrings.noBookingsMatched);
      }
    } catch (err) {
      setError(bookingStrings.verifyError);
    } finally {
      setLoading(false);
    }
  };

  const showForm = !isLoggedIn && !isSessionLoading;
  const showResults = bookings.length > 0;
  const normalizedSearch = search.trim().toLowerCase();
  const filteredBookings = useMemo(() => {
    if (!normalizedSearch) return bookings;
    return bookings.filter((booking) => {
      const address = booking.property.address || {};
      const addressText = [
        address.street,
        address.city,
        address.state,
        address.zipCode,
        address.country,
      ]
        .filter(Boolean)
        .join(" ");
      const searchText = [
        booking.id,
        booking.property.name,
        addressText,
        booking.guest.firstName,
        booking.guest.lastName,
        booking.guest.email,
        booking.status,
        booking.displayStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchText.includes(normalizedSearch);
    });
  }, [bookings, normalizedSearch]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      }),
    []
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {resolvedBookingId ? bookingStrings.reviewTitle : bookingStrings.accessTitle}
          </h1>
          {isLoggedIn && session?.user?.email && (
            <div className="mt-1 text-xs text-muted-foreground break-all">
              {session.user.email}
            </div>
          )}
        </div>
        {!isLoggedIn && (
          <Link
            href={loginUrl}
            className="rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-foreground hover:bg-ghost"
          >
            {bookingStrings.loginButton}
          </Link>
        )}
      </div>

      {showForm && (
        <div className="bg-card border border-border rounded-2xl shadow-sm p-6 space-y-4 text-card-foreground card-reveal">
          <div className="text-sm font-semibold text-foreground">{bookingStrings.verifyTitle}</div>
          <p className="text-sm text-muted-foreground">
            {resolvedBookingId
              ? bookingStrings.verifyThisBooking
              : bookingStrings.verifyAllBookings}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={bookingStrings.firstName}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={bookingStrings.lastName}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={bookingStrings.email}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={bookingStrings.phone}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          {error && <div className="text-sm text-destructive">{error}</div>}
          <button
            type="button"
            onClick={handleLookup}
            disabled={loading || !firstName || !lastName || !email || !phone}
            className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? bookingStrings.checking : bookingStrings.lookupButton}
          </button>
        </div>
      )}

      {isSessionLoading && (
        <div className="bg-card border border-border rounded-2xl shadow-sm p-6 text-sm text-muted-foreground card-reveal">
          {strings.common.buttons.loading}
        </div>
      )}

      {!showForm && !isSessionLoading && error && <div className="text-sm text-destructive">{error}</div>}

      {!showResults && !showForm && !isSessionLoading && !loading && !error && (
        <div className="text-sm text-muted-foreground">{bookingStrings.noBookingsAccount}</div>
      )}

      {!showResults && submitted && !loading && !error && showForm && (
        <div className="text-sm text-muted-foreground">{bookingStrings.noBookingsDetails}</div>
      )}

      {showResults && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <label className="text-sm text-muted-foreground flex-1 min-w-[240px]">
              {bookingStrings.searchLabel}
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={bookingStrings.searchPlaceholder}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </label>
            <div className="text-xs text-muted-foreground">
              {bookingStrings.resultsLabel(filteredBookings.length, bookings.length)}
            </div>
          </div>

          {filteredBookings.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              {bookingStrings.noSearchResults}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="sm:hidden space-y-3">
                {filteredBookings.map((booking) => {
                  const isExpanded = expandedRows.includes(booking.id);
                  const displayStatus = booking.displayStatus ?? booking.status;
                  const statusLabel =
                    (bookingStrings.statusLabels as Record<string, string> | undefined)?.[
                      displayStatus
                    ] ?? displayStatus.toLowerCase();
                  const statusClass = (() => {
                    switch (displayStatus) {
                      case "PENDING":
                        return "bg-accent/20 text-accent-foreground";
                      case "PARTIALLY_REFUNDED":
                        return "bg-accent/15 text-accent-foreground";
                      case "REFUNDED":
                        return "bg-accent/10 text-accent-foreground";
                      case "APPROVED":
                      case "CONFIRMED":
                        return "bg-primary/15 text-foreground";
                      case "CANCELLED":
                      case "EXPIRED":
                      case "COMPLETED":
                        return "bg-ghost text-ghost-foreground";
                      default:
                        return "bg-ghost text-ghost-foreground";
                    }
                  })();
                  const timeZone = booking.property.timeZone ?? "UTC";
                  const timeZoneLabel = formatTimeZoneLabel(timeZone, new Date(booking.checkIn));
                  const checkInTimeLabel = formatTimeWithZone(
                    booking.checkInTime,
                    timeZoneLabel
                  );
                  const checkOutTimeLabel = formatTimeWithZone(
                    booking.checkOutTime,
                    timeZoneLabel
                  );

                  return (
                    <div
                      key={booking.id}
                      className="rounded-2xl border border-border bg-card text-card-foreground shadow-sm p-4 space-y-3 card-reveal"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-foreground">{booking.property.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {bookingStrings.bookingId(booking.id)}
                          </div>
                        </div>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </div>
                      <div className="text-sm text-foreground">
                        <div>
                          {dateFormatter.format(new Date(booking.checkIn))} {checkInTimeLabel}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          → {dateFormatter.format(new Date(booking.checkOut))} {checkOutTimeLabel}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {bookingStrings.tableHeaders.guests}:{" "}
                        <span className="font-semibold text-foreground">{booking.numberOfGuests}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleRow(booking.id)}
                        className="w-full rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-ghost"
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? bookingStrings.collapseRow : bookingStrings.expandRow}
                      </button>
                      {isExpanded && (
                        <div className="pt-2">
                          <BookingDetailsCard
                            booking={booking}
                            verification={!isLoggedIn ? verifiedInfo : null}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="hidden sm:block rounded-2xl border border-border bg-card text-card-foreground shadow-sm overflow-hidden card-reveal">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-ghost text-ghost-foreground">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">
                          {bookingStrings.tableHeaders.booking}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          {bookingStrings.tableHeaders.stay}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          {bookingStrings.tableHeaders.status}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold">
                          {bookingStrings.tableHeaders.guests}
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          {bookingStrings.tableHeaders.action}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBookings.map((booking) => {
                        const isExpanded = expandedRows.includes(booking.id);
                        const displayStatus = booking.displayStatus ?? booking.status;
                        const statusLabel =
                          (bookingStrings.statusLabels as Record<string, string> | undefined)?.[
                            displayStatus
                          ] ?? displayStatus.toLowerCase();
                        const statusClass = (() => {
                          switch (displayStatus) {
                            case "PENDING":
                              return "bg-accent/20 text-accent-foreground";
                            case "PARTIALLY_REFUNDED":
                              return "bg-accent/15 text-accent-foreground";
                            case "REFUNDED":
                              return "bg-accent/10 text-accent-foreground";
                            case "APPROVED":
                            case "CONFIRMED":
                              return "bg-primary/15 text-foreground";
                            case "CANCELLED":
                            case "EXPIRED":
                            case "COMPLETED":
                              return "bg-ghost text-ghost-foreground";
                            default:
                              return "bg-ghost text-ghost-foreground";
                          }
                        })();
                        const timeZone = booking.property.timeZone ?? "UTC";
                        const timeZoneLabel = formatTimeZoneLabel(timeZone, new Date(booking.checkIn));
                        const checkInTimeLabel = formatTimeWithZone(
                          booking.checkInTime,
                          timeZoneLabel
                        );
                        const checkOutTimeLabel = formatTimeWithZone(
                          booking.checkOutTime,
                          timeZoneLabel
                        );

                        return (
                          <Fragment key={booking.id}>
                            <tr className="border-t border-border">
                              <td className="px-4 py-3">
                                <div className="font-semibold text-foreground">
                                  {booking.property.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {bookingStrings.bookingId(booking.id)}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground">
                                <div>
                                  {dateFormatter.format(new Date(booking.checkIn))} {checkInTimeLabel}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  → {dateFormatter.format(new Date(booking.checkOut))} {checkOutTimeLabel}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}
                                >
                                  {statusLabel}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-foreground">
                                {booking.numberOfGuests}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={() => toggleRow(booking.id)}
                                  className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-ghost"
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? bookingStrings.collapseRow : bookingStrings.expandRow}
                                </button>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="border-t border-border bg-background/40">
                                <td colSpan={5} className="px-4 py-4">
                                  <BookingDetailsCard
                                    booking={booking}
                                    verification={!isLoggedIn ? verifiedInfo : null}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
